import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { articles } from '../../../mocks/articles';
import ArticleSkeleton from './ArticleSkeleton';
import { useArticlePopularity } from '../../../hooks/useArticlePopularity';

interface ArticleListProps {
  onOpenModal: () => void;
}

const ALL_IDS = articles.map((a) => a.id);

/** ランキングバッジの設定 */
const RANK_CONFIG: Record<number, { label: string; bg: string; text: string; border: string; icon?: string }> = {
  1: { label: '1位', bg: 'bg-[#D4A96A]', text: 'text-white', border: 'border-[#C09050]', icon: 'ri-trophy-fill' },
  2: { label: '2位', bg: 'bg-[#B0BEC5]', text: 'text-white', border: 'border-[#90A4AE]', icon: 'ri-medal-fill' },
  3: { label: '3位', bg: 'bg-[#A0785A]', text: 'text-white', border: 'border-[#8B6547]', icon: 'ri-award-fill' },
};

/** カテゴリごとのカラー設定 */
const CATEGORY_COLOR: Record<string, { bg: string; text: string; border: string }> = {
  '知識をつける':      { bg: 'bg-[#EBF5FF]',  text: 'text-[#3A70B0]',  border: 'border-[#C0D8F0]' },
  '予算を決める':      { bg: 'bg-[#FFF4E8]',  text: 'text-[#B07030]',  border: 'border-[#F0D8B0]' },
  'パートナーを選ぶ':  { bg: 'bg-[#EAF7EE]',  text: 'text-[#387A50]',  border: 'border-[#B8DEC4]' },
  '物件・プランを決める': { bg: 'bg-[#FFF0EB]', text: 'text-[#B05030]', border: 'border-[#F0C8B8]' },
  '契約・引き渡し':    { bg: 'bg-[#FFF0F0]',  text: 'text-[#A03040]',  border: 'border-[#F0C0C8]' },
};

/** 記事ラベルの設定（記事IDに対応） */
const ARTICLE_LABELS: Record<number, { text: string; bg: string; text_color: string }> = {
  7:  { text: '初心者向け', bg: 'bg-[#8FA882]', text_color: 'text-white' },
  27: { text: '保存版',     bg: 'bg-[#D4A96A]', text_color: 'text-white' },
  41: { text: '図解あり',   bg: 'bg-[#7A9A8A]', text_color: 'text-white' },
  9:  { text: '注意点',     bg: 'bg-[#C07070]', text_color: 'text-white' },
  21: { text: '初心者向け', bg: 'bg-[#8FA882]', text_color: 'text-white' },
  40: { text: '保存版',     bg: 'bg-[#D4A96A]', text_color: 'text-white' },
  35: { text: '完全版',     bg: 'bg-[#6A8AA0]', text_color: 'text-white' },
  36: { text: '図解あり',   bg: 'bg-[#7A9A8A]', text_color: 'text-white' },
};

interface RankBadgeProps {
  rank: number;
}

function RankBadge({ rank }: RankBadgeProps) {
  const top3 = RANK_CONFIG[rank];
  if (top3) {
    return (
      <div
        className={`absolute top-2.5 left-2.5 z-10 flex items-center gap-1 px-2.5 py-1 rounded-full ${top3.bg} ${top3.text} border ${top3.border} text-xs font-bold`}
        aria-label={`人気ランキング${top3.label}`}
      >
        <i className={`${top3.icon} text-sm`} aria-hidden="true" />
        {top3.label}
      </div>
    );
  }
  // 4〜6位
  return (
    <div
      className="absolute top-2.5 left-2.5 z-10 flex items-center justify-center w-7 h-7 rounded-full bg-white/80 text-[#5A5A5A] text-xs font-bold border border-white/60"
      aria-label={`人気ランキング${rank}位`}
    >
      {rank}
    </div>
  );
}

/** カテゴリカラータグ */
function CategoryTag({ category }: { category: string }) {
  const colors = CATEGORY_COLOR[category] ?? { bg: 'bg-[#E8F0E6]', text: 'text-[#5A7A52]', border: 'border-[#C8D8C0]' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-[10px] font-semibold ${colors.bg} ${colors.text} ${colors.border}`}>
      {category}
    </span>
  );
}

/** 記事ラベル（サムネイル右下） */
function ArticleLabel({ articleId }: { articleId: number }) {
  const label = ARTICLE_LABELS[articleId];
  if (!label) return null;
  return (
    <div className={`absolute bottom-2 right-2 z-10 px-2 py-0.5 rounded-md text-[9px] font-bold ${label.bg} ${label.text_color}`}>
      {label.text}
    </div>
  );
}

export default function ArticleList({ onOpenModal }: ArticleListProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const { sortedByPopularity, trackClick } = useArticlePopularity(ALL_IDS);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 480);
    return () => clearTimeout(timer);
  }, []);

  // クリック数順で上位6件
  const popularIds = sortedByPopularity.slice(0, 6);
  const popularArticles = popularIds
    .map((id) => articles.find((a) => a.id === id))
    .filter(Boolean) as typeof articles;

  const firstRow = popularArticles.slice(0, 3);
  const secondRow = popularArticles.slice(3, 6);

  const handleCardClick = (articleId: number, e: React.MouseEvent) => {
    e.preventDefault();
    trackClick(articleId);
    navigate(`/articles/${articleId}`);
  };

  return (
    <section
      id="articles"
      aria-labelledby="articles-heading"
      className="py-20 md:py-28 bg-[#F5F1ED]"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#E8DDD0] text-[#8A7040] text-xs font-semibold tracking-wide mb-3">
              <i className="ri-fire-line" aria-hidden="true" />
              人気記事（週間ランキング）
            </div>
            <h2
              id="articles-heading"
              className="text-2xl md:text-3xl font-bold text-[#2C2C2C] leading-tight"
            >
              人気の記事をチェック
            </h2>
            <p className="text-[#6B6B6B] text-sm mt-1.5">
              鳥取の家づくりで知っておきたい情報をまとめました
            </p>
          </div>
          <a
            href="/steps/01"
            onClick={(e) => { e.preventDefault(); navigate('/steps/01'); }}
            className="inline-flex items-center gap-2 text-[#8FA882] text-sm font-semibold hover:text-[#6A7A60] transition-colors cursor-pointer whitespace-nowrap self-start md:self-auto"
          >
            記事一覧を見る
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </a>
        </div>

        {/* First 3 Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-7">
          {isLoading
            ? Array.from({ length: 3 }).map((_, i) => <ArticleSkeleton key={i} />)
            : firstRow.map((article, idx) => (
              <a
                key={article.id}
                href={`/articles/${article.id}`}
                onClick={(e) => handleCardClick(article.id, e)}
                className="bg-white rounded-2xl overflow-hidden cursor-pointer group transition-transform duration-200 hover:-translate-y-1 focus-within:ring-2 focus-within:ring-[#8FA882] no-underline block"
                aria-label={`記事「${article.title}」を読む`}
              >
                {/* Breadcrumb */}
                <div className="px-4 pt-3.5 pb-0">
                  <nav aria-label="パンくずリスト">
                    <ol
                      className="flex items-center gap-1 text-[10px] text-[#A0A0A0]"
                      itemScope
                      itemType="https://schema.org/BreadcrumbList"
                    >
                      <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                        <span className="hover:text-[#8FA882] transition-colors" itemProp="name">ホーム</span>
                        <meta itemProp="position" content="1" />
                      </li>
                      <li aria-hidden="true"><span className="mx-1">/</span></li>
                      <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                        <span className="text-[#6B6B6B]" itemProp="name">{article.category}</span>
                        <meta itemProp="position" content="2" />
                      </li>
                    </ol>
                  </nav>
                </div>

                {/* Thumbnail */}
                <div className="relative mt-3 mx-3.5 rounded-xl overflow-hidden h-40 bg-[#EDEAE6]">
                  <RankBadge rank={idx + 1} />
                  <ArticleLabel articleId={article.id} />
                  <img
                    src={article.image}
                    alt={article.title}
                    loading="lazy"
                    decoding="async"
                    width="400"
                    height="160"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                {/* Body */}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2.5 flex-wrap">
                    <CategoryTag category={article.category} />
                    <span className="text-[#A0A0A0] text-[10px]">{article.date}</span>
                    <span className="text-[#A0A0A0] text-[10px] ml-auto flex items-center gap-0.5">
                      <i className="ri-time-line text-xs" aria-hidden="true" />
                      {article.readTime}
                    </span>
                  </div>
                  <h3 className="text-[#2C2C2C] text-sm font-bold leading-snug line-clamp-2 mb-2 group-hover:text-[#8FA882] transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-[#6B6B6B] text-xs leading-relaxed line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex flex-wrap gap-1 mb-3.5">
                    {article.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="px-2 py-0.5 rounded-md bg-[#F5F1ED] text-[#8A7040] text-[10px]">
                        #{tag}
                      </span>
                    ))}
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[#8FA882] text-xs font-semibold group-hover:gap-2.5 transition-all duration-200 whitespace-nowrap">
                    続きを読む
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                  </span>
                </div>
              </a>
            ))}
        </div>

        {/* Inline CTA Banner */}
        <div className="rounded-2xl overflow-hidden mb-7">
          <div className="relative p-6 md:p-8">
            <div className="absolute inset-0">
              <img
                src="https://public.readdy.ai/ai/img_res/108c42b2f68a2f986921abab9a28ee8a.jpg"
                alt=""
                loading="lazy"
                decoding="async"
                width="1200"
                height="300"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-[#3D5C38]/85" />
            </div>
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 text-white/90 text-[10px] font-semibold mb-3">
                  <i className="ri-gift-line" aria-hidden="true" />
                  完全無料
                </div>
                <h3 className="text-white font-bold text-lg md:text-xl leading-snug mb-1">
                  鳥取で家を建てるなら<br className="md:hidden" />まず資料請求
                </h3>
                <p className="text-white/80 text-sm">
                  間取り・費用・工務店情報を一括でお届け。しつこい営業は一切なし。
                </p>
              </div>
              <button
                type="button"
                onClick={onOpenModal}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#D4A96A] text-white font-bold text-sm hover:bg-[#C09050] transition-colors duration-200 cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                <i className="ri-file-list-3-line text-base" aria-hidden="true" />
                資料請求はこちら（無料）
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>

        {/* Remaining 3 Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {isLoading
            ? Array.from({ length: 3 }).map((_, i) => <ArticleSkeleton key={i} />)
            : secondRow.map((article, idx) => (
              <a
                key={article.id}
                href={`/articles/${article.id}`}
                onClick={(e) => handleCardClick(article.id, e)}
                className="bg-white rounded-2xl overflow-hidden cursor-pointer group transition-transform duration-200 hover:-translate-y-1 focus-within:ring-2 focus-within:ring-[#8FA882] no-underline block"
                aria-label={`記事「${article.title}」を読む`}
              >
                <div className="px-4 pt-3.5 pb-0">
                  <nav aria-label="パンくずリスト">
                    <ol className="flex items-center gap-1 text-[10px] text-[#A0A0A0]">
                      <li><span className="hover:text-[#8FA882] transition-colors">ホーム</span></li>
                      <li aria-hidden="true"><span className="mx-1">/</span></li>
                      <li><span className="text-[#6B6B6B]">{article.category}</span></li>
                    </ol>
                  </nav>
                </div>
                <div className="relative mt-3 mx-3.5 rounded-xl overflow-hidden h-40 bg-[#EDEAE6]">
                  <RankBadge rank={idx + 4} />
                  <ArticleLabel articleId={article.id} />
                  <img
                    src={article.image}
                    alt={article.title}
                    loading="lazy"
                    decoding="async"
                    width="400"
                    height="160"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2.5 flex-wrap">
                    <CategoryTag category={article.category} />
                    <span className="text-[#A0A0A0] text-[10px]">{article.date}</span>
                    <span className="text-[#A0A0A0] text-[10px] ml-auto flex items-center gap-0.5">
                      <i className="ri-time-line text-xs" aria-hidden="true" />
                      {article.readTime}
                    </span>
                  </div>
                  <h3 className="text-[#2C2C2C] text-sm font-bold leading-snug line-clamp-2 mb-2 group-hover:text-[#8FA882] transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-[#6B6B6B] text-xs leading-relaxed line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex flex-wrap gap-1 mb-3.5">
                    {article.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="px-2 py-0.5 rounded-md bg-[#F5F1ED] text-[#8A7040] text-[10px]">
                        #{tag}
                      </span>
                    ))}
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[#8FA882] text-xs font-semibold group-hover:gap-2.5 transition-all duration-200 whitespace-nowrap">
                    続きを読む
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                  </span>
                </div>
              </a>
            ))}
        </div>
      </div>
    </section>
  );
}
